var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ApiClient } from '../api';
import { EventEmitter } from './event-emitter';
// Per ADR-001: the game bundle ships ONLY the banner renderer (GPT banners
// must live inside the iframe since the parent can't position an ad inside a
// cross-origin frame). Interstitial / video / rewarded rendering moves to the
// host shell on getjar.com — those files are imported by the host bundle, not
// here. Do NOT add GPTInterstitialAds / IMA* imports back to this file.
import { GPTLoader, GPTBannerAds } from '../ads';
import { resolveBannerSize } from '../ads/gpt-banner';
import { AdScoreClient } from '../adscore';
import { AdEventType, } from '../types';
import { TargetingBuilder } from '../utils/targeting-builder';
import { BridgeClient } from '../bridge/bridge-client';
/**
 * GetJar Ads SDK
 * Main SDK class for HTML5 game monetization
 */
export class GetJarAdsSDK {
    constructor() {
        this.apiClient = null;
        this.eventEmitter = null;
        this.bannerAds = null;
        // Full-screen renderers (GPT interstitial, IMA video, IMA rewarded) now
        // live on the host shell. The game bundle holds only a bridge to them.
        this.bridge = null;
        this.sessionId = null;
        this.appId = '';
        this.publisher = '';
        this.networkId = '';
        // Ad-source switch returned by /ads/init. 'gam' (default) skips the prebid
        // hop entirely; 'ay' triggers a POST /ads/bid before each banner/interstitial
        // slot definition. Per-format opt-in is resolved server-side at bid time.
        this.adProvider = 'gam';
        // Server-pushed AY diagnostic mode (mirrors backend AY_PBS_DEBUG). Gates
        // SDK-side console output on the in-iframe banner bid path.
        this.ayDebug = false;
        this.isInitialized = false;
        this.initConfig = null;
        this.initFailureCount = 0;
        // Preload state for interstitial and rewarded ads
        this.preloadState = {
            interstitial: { loading: false, ready: false, error: null },
            rewarded: { loading: false, ready: false, error: null },
        };
        /**
         * Sole production backend. Partners get this by default; only the local
         * dev harness overrides via `config.apiUrl`.
         */
        this.DEFAULT_API_URL = 'https://ads.getjar.com';
        this.adUnits = {
            banner: '',
            interstitial: '',
        };
        // Video configuration from backend (opaque to partners)
        // Backend provides VAST tags or other video ad configuration
        this.videoConfig = null;
        // Targeting builder for consistent targeting parameter construction
        this.targetingBuilder = null;
        // AdScore (invalid-traffic detection). Off by default; opt-in via init({ adscore }).
        // When in 'block' mode and verdict is invalid, all ad methods short-circuit.
        // When in 'tag' mode, the verdict rides along as `adscore_verdict` targeting.
        this.adscore = new AdScoreClient();
    }
    // Interstitial pacing now lives on the host (ADR-001) — the host sees the
    // full session across game switches and is the only side that can enforce
    // a cap that survives an iframe reload. The game bundle no longer holds a
    // PacingGate; `ad_result.outcome === 'suppressed'` from the bridge is the
    // sole signal that pacing blocked a show.
    /**
     * Initialize the SDK
     * Note: Domain validation is performed server-side via Origin/Referer headers
     */
    init(config) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (this.isInitialized) {
                console.warn('[GetJarAds] SDK already initialized');
                return;
            }
            // Store config for potential re-initialization
            this.initConfig = config;
            // Store app and publisher IDs
            this.appId = config.appId;
            this.publisher = config.publisher;
            // Determine API URL. The default is the production backend; the
            // override is only used by the local dev harness.
            const apiUrl = config.apiUrl || this.DEFAULT_API_URL;
            // Create API client and event emitter
            this.apiClient = new ApiClient(apiUrl);
            this.eventEmitter = new EventEmitter(this.apiClient);
            // Set ad context provider so all events automatically include context
            this.eventEmitter.setAdContextProvider(() => this.getAdContext());
            try {
                // Call backend init to get network ID and ad units
                const response = yield this.apiClient.init(config.appId, config.publisher, {
                    privacy: config.privacy,
                });
                this.sessionId = response.sessionId;
                this.eventEmitter.setSessionId(response.sessionId);
                // Get network ID and ad units from backend response
                this.networkId = response.networkId;
                this.adProvider = response.adProvider === 'ay' ? 'ay' : 'gam';
                this.ayDebug = response.ayDebug === true;
                if (this.ayDebug) {
                    console.log(`[GetJarAds AY DEBUG] verbose AY logging is ON (server-pushed). adProvider=${this.adProvider}`);
                }
                // Pacing config in the /ads/init response is now consumed by the host
                // shell (see HostShell.reconfigurePacing in sdk/src/host/host-shell.ts).
                // The game-side SDK no longer applies it.
                // Use ad units from backend, but allow config overrides
                if (config.adUnits) {
                    this.adUnits = Object.assign(Object.assign({}, response.adUnits), config.adUnits);
                }
                else {
                    this.adUnits = response.adUnits;
                }
                // Store video configuration from backend (for video ads)
                this.videoConfig = response.videoConfig || null;
                // Initialize targeting builder with app and publisher IDs
                this.targetingBuilder = new TargetingBuilder(this.appId, this.publisher);
                // Detect environment metadata once per session (no PII — UA-string heuristic only)
                this.detectEnvironmentMetadata();
                // Load GPT (banner) and AdScore in parallel. AdScore activation is
                // controlled by the backend (presence of `response.adscore.apiKey`);
                // partners never opt in or out. Fails open: a third-party IVT outage
                // cannot deny real users their ads — `loadAndWait` resolves
                // successfully even on script failure. `AdScoreClient` needs
                // `apiClient` + `sessionId` to round-trip the signature it captures
                // from the JS API to `/ads/adscore/verify`.
                yield Promise.all([
                    GPTLoader.load(config.privacy),
                    this.adscore.loadAndWait(response.adscore, this.apiClient, this.sessionId),
                ]);
                // Banner is the ONLY renderer the game bundle keeps — see ADR-001.
                // Full-screen formats (interstitial / video / rewarded) route to the
                // host shell via the bridge instead of being rendered in-iframe.
                this.bannerAds = new GPTBannerAds(this.eventEmitter, this.adUnits.banner);
                // Bring the host bridge up. Only attempts a handshake when the SDK is
                // actually inside an iframe — outside one there is no parent to talk
                // to, so the bridge stays inactive and full-screen show* calls resolve
                // with no_ad/no_host (logged as an empty fill).
                yield this.startBridge(config);
                this.isInitialized = true;
                console.log('[GetJarAds] SDK initialized successfully', {
                    sessionId: response.sessionId,
                    expiresAt: response.expiresAt,
                    adUnits: this.adUnits,
                    bridgeActive: (_b = (_a = this.bridge) === null || _a === void 0 ? void 0 : _a.isActive()) !== null && _b !== void 0 ? _b : false,
                });
            }
            catch (error) {
                console.error('[GetJarAds] Failed to initialize SDK:', error);
                // Fail safely - SDK should not break game
                this.isInitialized = false;
                this.initFailureCount++;
                throw error;
            }
        });
    }
    /**
     * Re-initialize the SDK after a failure
     * Resets internal state and attempts to initialize again
     */
    reinit() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.initConfig) {
                throw new Error('Cannot reinit: SDK was never initialized. Call init() first.');
            }
            if (this.initFailureCount >= GetJarAdsSDK.MAX_INIT_RETRIES) {
                throw new Error(`Cannot reinit: Maximum retry attempts (${GetJarAdsSDK.MAX_INIT_RETRIES}) exceeded`);
            }
            console.log(`[GetJarAds] Re-initializing SDK (attempt ${this.initFailureCount + 1}/${GetJarAdsSDK.MAX_INIT_RETRIES})...`);
            // Reset state
            this.reset();
            // Try to initialize again
            yield this.init(this.initConfig);
        });
    }
    /**
     * Reset SDK state (for re-initialization)
     * Does not clear the stored config
     */
    reset() {
        this.apiClient = null;
        this.eventEmitter = null;
        this.bannerAds = null;
        if (this.bridge) {
            this.bridge.destroy();
            this.bridge = null;
        }
        this.sessionId = null;
        this.adProvider = 'gam';
        this.ayDebug = false;
        this.isInitialized = false;
        this.preloadState = {
            interstitial: { loading: false, ready: false, error: null },
            rewarded: { loading: false, ready: false, error: null },
        };
        // Reset GPT loader (banner). IMA is no longer loaded by the game bundle.
        GPTLoader.reset();
    }
    /**
     * Preload interstitial ad for faster display
     * Call this during game loading or between levels
     */
    preloadInterstitial() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.ensureInitialized()) {
                throw new Error('SDK not initialized');
            }
            if (this.preloadState.interstitial.loading) {
                console.log('[GetJarAds] Interstitial preload already in progress');
                return;
            }
            if (this.preloadState.interstitial.ready) {
                console.log('[GetJarAds] Interstitial already preloaded');
                return;
            }
            this.preloadState.interstitial.loading = true;
            this.preloadState.interstitial.error = null;
            try {
                // Interstitial rendering lives on the host (ADR-001). Game-side
                // preload is a no-op pacifier: it queries the host for readiness so
                // game UI can light up the "ready" flag, but does not preload any
                // renderer in this bundle. When the bridge is inactive we mark ready
                // anyway — show* will resolve immediately with no_ad/no_host and the
                // game keeps moving.
                if (this.bridge) {
                    const state = yield this.bridge.queryReady({ format: 'interstitial' });
                    this.preloadState.interstitial.ready = state.ready;
                }
                else {
                    this.preloadState.interstitial.ready = true;
                }
                console.log('[GetJarAds] Interstitial preload queried (host-side)');
            }
            catch (error) {
                this.preloadState.interstitial.error = error instanceof Error ? error : new Error(String(error));
                console.error('[GetJarAds] Interstitial preload failed:', error);
                throw error;
            }
            finally {
                this.preloadState.interstitial.loading = false;
            }
        });
    }
    /**
     * Preload rewarded ad for faster display
     * Call this during game loading or between levels
     */
    preloadRewarded() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.ensureInitialized()) {
                throw new Error('SDK not initialized');
            }
            if (this.preloadState.rewarded.loading) {
                console.log('[GetJarAds] Rewarded preload already in progress');
                return;
            }
            if (this.preloadState.rewarded.ready) {
                console.log('[GetJarAds] Rewarded already preloaded');
                return;
            }
            this.preloadState.rewarded.loading = true;
            this.preloadState.rewarded.error = null;
            try {
                // Same host-side semantics as preloadInterstitial — see comment there.
                // Per ADR-001, rewarded readiness is what gates the partner game's
                // reward-offer button, so this should reflect the host's truth.
                if (this.bridge) {
                    const state = yield this.bridge.queryReady({ format: 'rewarded_video' });
                    this.preloadState.rewarded.ready = state.ready;
                }
                else {
                    this.preloadState.rewarded.ready = false;
                }
                console.log('[GetJarAds] Rewarded preload queried (host-side)');
            }
            catch (error) {
                this.preloadState.rewarded.error = error instanceof Error ? error : new Error(String(error));
                console.error('[GetJarAds] Rewarded preload failed:', error);
                throw error;
            }
            finally {
                this.preloadState.rewarded.loading = false;
            }
        });
    }
    /**
     * Check if interstitial is preloaded and ready
     */
    isInterstitialReady() {
        return this.preloadState.interstitial.ready;
    }
    /**
     * Check if rewarded is preloaded and ready
     */
    isRewardedReady() {
        return this.preloadState.rewarded.ready;
    }
    /**
     * Get preload error (if any)
     */
    getPreloadError(type) {
        return this.preloadState[type].error;
    }
    /**
     * Detect platform / device-type / orientation once at init.
     * UA-string heuristics only — no IP, no user identifiers, no precise UA string is forwarded.
     */
    detectEnvironmentMetadata() {
        if (typeof window === 'undefined' || typeof navigator === 'undefined')
            return;
        try {
            const ua = navigator.userAgent || '';
            this.detectedPlatform = navigator.platform || undefined;
            this.detectedDeviceType = /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? 'mobile' : 'desktop';
            this.detectedOrientation = window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait';
        }
        catch (_a) {
            // Best-effort; do not block init if detection fails
        }
    }
    /**
     * Build the TargetingExtras object passed to the builder.
     * Merges per-call config with auto-detected env metadata.
     */
    buildExtras(config) {
        var _a;
        return {
            placementName: (_a = config.placementName) !== null && _a !== void 0 ? _a : config.placement,
            category: config.category,
            publisherId: config.publisherId,
            appId: config.appId,
            gameId: config.gameId,
            platform: this.detectedPlatform,
            deviceType: this.detectedDeviceType,
            orientation: this.detectedOrientation,
        };
    }
    /**
     * Apply the AdScore verdict to a targeting object (no-op when AdScore is off /
     * tagging is disabled). Verdict is also evaluated by `shouldBlockForAdscore()`
     * separately — this method only handles the `tag` mode side-channel.
     */
    withAdscoreTargeting(targeting) {
        return Object.assign(Object.assign({}, targeting), this.adscore.toTargeting());
    }
    /**
     * Run an AY S2S prebid auction and return the winning bid's targeting keys
     * (`hb_pb`, `hb_bidder`, `hb_size`, `hb_cache_id`, …). Caller merges these on
     * top of the base GAM targeting before `slot.setTargeting(...)`.
     *
     * Short-circuits to `{}` when:
     *   - `/ads/init` returned `adProvider: 'gam'` (no per-app AY opt-in)
     *   - The API client isn't available (defensive; should never trigger post-init)
     *   - The backend returns `hasBid: false` for any reason — env off, per-app
     *     off, per-format unconfigured, PBS error, PBS timeout, no winning bid.
     *
     * Fail-open by design: bid failures must never block an ad request.
     */
    getBidTargeting(format_1, sizes_1, adUnit_1, placement_1) {
        return __awaiter(this, arguments, void 0, function* (format, sizes, adUnit, placement, extras = {}) {
            var _a, _b, _c, _d, _e;
            if (this.adProvider !== 'ay' || !this.apiClient || !this.sessionId)
                return {};
            const bidInput = {
                sessionId: this.sessionId,
                appId: this.appId,
                publisherId: this.publisher,
                format,
                sizes,
                placement,
                adUnit,
                // Attribution-only fields (AY valuation + reporting). publisher.id on the
                // oRTB request stays = AY_ORG_ID; these never feed GAM line-item targeting.
                gameId: (_a = extras.gameId) !== null && _a !== void 0 ? _a : this.appId,
                category: extras.category,
                source: GetJarAdsSDK.SDK_SOURCE,
                privacy: (_b = this.initConfig) === null || _b === void 0 ? void 0 : _b.privacy,
                adContext: this.getAdContext(),
                // Real viewport at request time — backend maps to oRTB device.w/h.
                // Distinct from imp sizes[]; some bidders geo/target off screen class.
                viewport: this.viewportSize(),
                // Low-entropy user-agent client hints (Chromium only). Omitted on
                // Safari/Firefox — backend falls back to UA-string sniffing there.
                sua: this.collectLowEntropySua(),
            };
            const startedAt = Date.now();
            if (this.ayDebug) {
                console.log('[GetJarAds AY DEBUG] /ads/bid OUT', bidInput);
            }
            const response = yield this.apiClient.requestBids(bidInput);
            if (this.ayDebug) {
                const keys = response.hasBid ? Object.keys(response.targeting) : [];
                console.log(`[GetJarAds AY DEBUG] /ads/bid IN — ${Date.now() - startedAt}ms — ` +
                    `hasBid=${response.hasBid} bidder=${(_c = response.bidder) !== null && _c !== void 0 ? _c : '(none)'} ` +
                    `cpm=${(_d = response.cpm) !== null && _d !== void 0 ? _d : 'n/a'} ${(_e = response.currency) !== null && _e !== void 0 ? _e : ''} ` +
                    `keys=${keys.length === 0 ? '(empty)' : keys.join(',')}`, response.targeting);
            }
            return response.hasBid ? response.targeting : {};
        });
    }
    /**
     * Viewport size used both as the interstitial video imp `sizes[]` entry AND
     * as the top-level `viewport` on the bid request (→ oRTB device.w/h). GPT
     * renders out-of-page interstitials fullscreen; reporting the actual
     * viewport gives AY's PBS an honest size signal. SSR-safe.
     */
    viewportSize() {
        if (typeof window === 'undefined')
            return { w: 0, h: 0 };
        return { w: window.innerWidth || 0, h: window.innerHeight || 0 };
    }
    /**
     * Read low-entropy user-agent client hints from `navigator.userAgentData`.
     * Sync, no permission prompt. Returns `undefined` in browsers that don't
     * expose the API (Safari, Firefox) so the backend knows to fall back to
     * UA-string sniffing rather than treating an empty object as "no hints".
     *
     * We deliberately do NOT call `getHighEntropyValues()` — that's async +
     * needs `Accept-CH` opt-in and would add a permission surface for
     * negligible bidder value at the current scale.
     */
    collectLowEntropySua() {
        if (typeof navigator === 'undefined')
            return undefined;
        const uaData = navigator.userAgentData;
        if (!uaData)
            return undefined;
        return {
            browsers: Array.isArray(uaData.brands)
                ? uaData.brands.map((b) => {
                    var _a, _b;
                    return ({
                        brand: String((_a = b === null || b === void 0 ? void 0 : b.brand) !== null && _a !== void 0 ? _a : ''),
                        version: [String((_b = b === null || b === void 0 ? void 0 : b.version) !== null && _b !== void 0 ? _b : '')],
                    });
                })
                : undefined,
            mobile: uaData.mobile ? 1 : 0,
            platform: uaData.platform ? { brand: String(uaData.platform) } : undefined,
        };
    }
    /**
     * Returns true when AdScore is in `block` mode and the verdict is invalid.
     * Ad-show methods short-circuit on this; we log once per call so partners can
     * see in console why an ad request didn't go out.
     */
    shouldBlockForAdscore(placement) {
        if (!this.adscore.shouldBlock())
            return false;
        console.log(`[GetJarAds] AdScore blocked ad request${placement ? ` for ${placement}` : ''} — invalid traffic verdict.`);
        return true;
    }
    // --- Host bridge (ADR-001) ----------------------------------------------
    /**
     * Stand up the postMessage bridge to the getjar.com host shell. Only fires
     * when the SDK is loaded inside an iframe — outside one there is no
     * parent to talk to. A failed handshake (timeout / no host) is NOT an
     * error: the bridge stays inactive and future full-screen show* calls
     * resolve with no_ad/no_host.
     */
    startBridge(config) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.getAdContext() !== 'IN_GAME') {
                // Not in an iframe — production game frames are always embedded by
                // getjar.com, so this is the dev / direct-load case. Skip handshake.
                return;
            }
            // `hostOrigin` is required for the bridge to know whom to trust. Without
            // it we cannot safely accept any host reply, so we keep the bridge off.
            if (!config.hostOrigin) {
                console.warn('[GetJarAds] No hostOrigin configured — host bridge disabled. ' +
                    'Full-screen show* will resolve with no_ad/no_host.');
                return;
            }
            this.bridge = new BridgeClient({
                hostOrigin: config.hostOrigin,
                handshakeTimeoutMs: config.bridgeHandshakeTimeoutMs,
            });
            const acked = yield this.bridge.init(this.appId, this.publisher);
            if (!acked) {
                console.warn('[GetJarAds] Host handshake timed out — full-screen show* will resolve no_ad/no_host.');
            }
        });
    }
    /**
     * Common path for the three full-screen formats that resolve to `void`
     * (interstitial + video_interstitial). Posts a `request_show` and observes
     * the host's `ad_result`. The reward path lives in showRewardedVideo —
     * it needs the granted/amount fields.
     */
    routeFullScreenShow(format, config) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            if (!this.ensureInitialized())
                return;
            if (this.shouldBlockForAdscore((_a = config.placementName) !== null && _a !== void 0 ? _a : config.placement))
                return;
            console.log(`[GetJarAds] show ${format} called via bridge:`, config);
            const placement = (_b = config.placementName) !== null && _b !== void 0 ? _b : config.placement;
            const result = yield this.bridgeShow(format, {
                placementName: placement,
                category: config.category,
                gameId: config.gameId,
            });
            if (result.outcome === 'no_ad' && result.reason === 'no_host') {
                this.logEmptyFill(format, placement, 'no_host');
            }
            if (result.outcome === 'suppressed') {
                // Host-side pacing gate blocked the show. Mirror the same partner-
                // visible side effects the game-side gate used to produce: a console
                // line, the AD_SUPPRESSED telemetry event, and the onSuppressed
                // callback. The reason string is whatever the host returned (today
                // one of warmup / min_interval / hourly_cap from PacingGate).
                console.warn(`[GetJarAds] Interstitial suppressed by host pacing (${result.reason})${placement ? ` for ${placement}` : ''}.`);
                if (this.eventEmitter) {
                    // Fire-and-forget: telemetry must not block the game.
                    void this.eventEmitter.emit(AdEventType.AD_SUPPRESSED, {
                        placement,
                        adType: format,
                        reason: result.reason,
                    });
                }
                if (config.onSuppressed) {
                    try {
                        config.onSuppressed({
                            reason: ((_c = result.reason) !== null && _c !== void 0 ? _c : 'unknown'),
                            placementName: placement,
                            adType: format,
                        });
                    }
                    catch (err) {
                        console.warn('[GetJarAds] onSuppressed callback threw, ignoring:', err);
                    }
                }
            }
            if (result.outcome === 'error' && config.onError) {
                try {
                    config.onError(new Error(result.reason || 'host_error'));
                }
                catch (err) {
                    console.warn('[GetJarAds] onError callback threw:', err);
                }
            }
        });
    }
    /**
     * Lowest layer of the bridge call path. Builds the `request_show` payload
     * and resolves to the host's `ad_result`. If the bridge is inactive the
     * client itself short-circuits to `{ outcome: 'no_ad', reason: 'no_host' }`
     * — see BridgeClient.requestShow.
     */
    bridgeShow(format, payload) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.bridge) {
                // Bridge wasn't created (not in iframe, or no hostOrigin). Same
                // no_ad/no_host shape so call sites have one branch to write.
                return { outcome: 'no_ad', reason: 'no_host' };
            }
            return this.bridge.requestShow(Object.assign({ format }, payload));
        });
    }
    /**
     * Emit an empty-fill log line — best-effort from the game side when the
     * host wasn't there to record it itself. Kept structured so it can be
     * grepped out of game-side console output during smoke tests.
     */
    logEmptyFill(format, placementName, reason) {
        console.log('[GetJarAds] empty_fill', { format, placementName, reason });
    }
    /**
     * Create a banner ad using GPT
     */
    createBanner(config) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (!this.ensureInitialized() || !this.bannerAds)
                return;
            if (this.shouldBlockForAdscore((_a = config.placementName) !== null && _a !== void 0 ? _a : config.placement))
                return;
            console.log('[GetJarAds] createBanner called:', config);
            try {
                const placement = (_b = config.placementName) !== null && _b !== void 0 ? _b : config.placement;
                // Pre-resolve the preset so the bid request reports the same size GPT
                // will request. Unresolvable sizes fall through to bannerAds.show() which
                // throws with a helpful message.
                const resolved = resolveBannerSize(config);
                const baseTargeting = this.withAdscoreTargeting(this.targetingBuilder.build('banner', this.buildExtras(config)));
                // AY bid runs only when adProvider='ay' AND the size resolved cleanly.
                // hb_* keys win over any equally-named base keys (none today, but safe).
                const bidTargeting = resolved
                    ? yield this.getBidTargeting('banner', [{ w: resolved.w, h: resolved.h }], this.adUnits.banner, placement, { category: config.category, gameId: config.gameId })
                    : {};
                const targeting = Object.assign(Object.assign({}, baseTargeting), bidTargeting);
                yield this.bannerAds.show(config, targeting);
            }
            catch (error) {
                console.error('[GetJarAds] Banner ad failed:', error);
                throw error;
            }
        });
    }
    /** Alias for createBanner — preferred forward-going name. */
    showBanner(config) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.createBanner(config);
        });
    }
    /**
     * Render a page-surround banner/MREC unit on the getjar.com parent page,
     * attributed to the currently-embedded game's developer (ADR-001 Part A).
     *
     * Same inventory product as the in-game banner — the only difference is
     * the `source` targeting key, which flips to `'on_site'` so GAM reporting
     * can roll revenue up under `ads_sdk_apps.revenue_share_on_site` instead
     * of the in-game rate. (The two rates may be set equal; they remain
     * separate columns purely as a pricing lever per the ADR.)
     *
     * Caller responsibilities:
     *   - Invoke from the parent page only (ON_SITE). A console warning fires
     *     if called from inside an iframe — the call still proceeds, since
     *     the contract is "the host page calls this; the source key is what
     *     the back-end keys revenue off."
     *   - Pass the currently-embedded game's `gameId` AND `publisherId` on
     *     the config. They are REQUIRED here (unlike showBanner, where init
     *     defaults apply) — the host shell page itself has no game identity,
     *     so attribution must come from the call site.
     */
    showOnPageBanner(config) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (!this.ensureInitialized() || !this.bannerAds)
                return;
            // Required attribution on this path: the host page has no game of its
            // own, so we won't fall back to init-time defaults that would attribute
            // to the host shell. Surface this loudly — it's a programming bug.
            if (!config.gameId || !config.publisherId) {
                throw new Error('showOnPageBanner requires both gameId and publisherId on the config — ' +
                    'they identify which embedded game this on-site impression attributes to.');
            }
            // Soft-warn if called from inside an iframe. We don't block: the
            // attribution is determined by the explicit gameId/publisherId in the
            // config and the source='on_site' key, both of which are correct
            // regardless of where the JS happens to run.
            if (this.getAdContext() !== 'ON_SITE') {
                console.warn('[GetJarAds] showOnPageBanner called from a non-ON_SITE context. ' +
                    'Page-surround units are intended for the parent page; targeting will ' +
                    'still carry source=on_site, but verify the call site is correct.');
            }
            if (this.shouldBlockForAdscore((_a = config.placementName) !== null && _a !== void 0 ? _a : config.placement))
                return;
            console.log('[GetJarAds] showOnPageBanner called:', config);
            try {
                const placement = (_b = config.placementName) !== null && _b !== void 0 ? _b : config.placement;
                const resolved = resolveBannerSize(config);
                // Same builder, same format. We override `source` to 'on_site' and
                // pin gameId / publisherId from the active embedded game — these
                // are mandatory at this entry, so the assert above already ran.
                const extras = Object.assign(Object.assign({}, this.buildExtras(config)), { source: 'on_site', gameId: config.gameId, publisherId: config.publisherId });
                const baseTargeting = this.withAdscoreTargeting(this.targetingBuilder.build('banner', extras));
                // AY bid still runs when enabled — the on-site path needs the same
                // hb_* keys to win line items. publisher.id stays = AY_ORG_ID; the
                // per-game attribution rides via FPD.
                const bidTargeting = resolved
                    ? yield this.getBidTargeting('banner', [{ w: resolved.w, h: resolved.h }], this.adUnits.banner, placement, { category: config.category, gameId: config.gameId })
                    : {};
                const targeting = Object.assign(Object.assign({}, baseTargeting), bidTargeting);
                yield this.bannerAds.show(config, targeting);
            }
            catch (error) {
                console.error('[GetJarAds] On-page banner failed:', error);
                throw error;
            }
        });
    }
    /**
     * Show a (display) interstitial. Per ADR-001 this routes to the host shell
     * over the postMessage bridge — the game bundle no longer renders this
     * format itself. Pacing, AdScore enforcement, and the actual GPT request
     * are all the host's job now; this side just dispatches.
     *
     * No-host edge case: when the bridge is inactive, resolves immediately
     * with `no_ad / no_host` (logged as an empty fill). Nothing renders.
     */
    createInterstitial(config) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.routeFullScreenShow('interstitial', config);
        });
    }
    /** Alias for createInterstitial — preferred forward-going name. */
    showInterstitial(config) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.createInterstitial(config);
        });
    }
    /**
     * Show a rewarded ad using GPT display.
     * @deprecated GPT display-rewarded is not provisioned in the current GAM setup.
     * Use `showRewardedVideo()` (IMA) for all reward flows.
     */
    showRewarded(config) {
        return __awaiter(this, void 0, void 0, function* () {
            // GPT display-rewarded was never provisioned in the current GAM setup
            // (see ads-app.entity / GAM_SETUP_CHECKLIST.md). The host bridge owns
            // every full-screen format now, so the right path for partners is
            // showRewardedVideo(). Surface the deprecation cleanly.
            console.warn('[GetJarAds] showRewarded() is deprecated and not provisioned. ' +
                'Use showRewardedVideo() instead.');
            if (config.onError) {
                config.onError(new Error('GPT display-rewarded not available; use showRewardedVideo()'));
            }
            // Best-effort reward callback so partner UIs that always read the
            // result get a deterministic response.
            config.onRewarded({
                type: config.rewardType,
                amount: 0,
                granted: false,
                reason: 'deprecated_method',
            });
        });
    }
    /**
     * Ensure SDK is initialized
     */
    ensureInitialized() {
        if (!this.isInitialized) {
            console.error('[GetJarAds] SDK not initialized. Call init() first.');
            return false;
        }
        return true;
    }
    /**
     * Get current session ID (for debugging)
     */
    getSessionId() {
        return this.sessionId;
    }
    /**
     * Get GAM network ID (for building VAST tags)
     * Returns the network ID provided by the backend during init
     * @deprecated This method exposes GAM implementation details and will be removed in a future version.
     * Video ads are now configured server-side. Use showRewardedVideo() and showInterstitialVideo() without VAST URL construction.
     */
    getNetworkId() {
        console.warn('[GetJarAds] DEPRECATED: getNetworkId() exposes GAM implementation details and will be removed. Video ads are now backend-configured.');
        if (!this.isInitialized) {
            console.warn('[GetJarAds] SDK not initialized. Call init() first.');
            return '';
        }
        return this.networkId;
    }
    /**
     * Check if SDK is initialized
     */
    isReady() {
        return this.isInitialized;
    }
    /**
     * Show a non-rewarded video interstitial. Per ADR-001 this routes to the
     * host shell over the postMessage bridge — the host owns the IMA pipeline,
     * pacing gate, and AdScore enforcement. Game-side: dispatch only.
     *
     * No-host edge case: resolves immediately with `no_ad / no_host` (logged
     * as an empty fill) so the game keeps moving.
     */
    showVideoInterstitial(config) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.routeFullScreenShow('video_interstitial', config);
        });
    }
    /**
     * @deprecated Use `showVideoInterstitial()` instead. Kept for backwards compatibility.
     */
    showInterstitialVideo(config) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.showVideoInterstitial(config);
        });
    }
    /**
     * Show a rewarded video. Per ADR-001 this routes to the host shell over
     * the postMessage bridge — the host runs IMA, observes the COMPLETE event,
     * and calls `/ads/reward/validate` directly. The bridge returns whether
     * the reward was granted via `ad_result { outcome, granted, amount }`.
     *
     * No-host edge case: resolves with a non-granted Reward and reason
     * `no_host`; nothing renders, empty fill logged.
     */
    showRewardedVideo(config) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e;
            if (!this.ensureInitialized())
                return;
            if (this.shouldBlockForAdscore((_a = config.placementName) !== null && _a !== void 0 ? _a : config.placement))
                return;
            console.log('[GetJarAds] showRewardedVideo called:', config);
            const result = yield this.bridgeShow('rewarded_video', {
                placementName: (_b = config.placementName) !== null && _b !== void 0 ? _b : config.placement,
                rewardType: config.rewardType,
                category: config.category,
                gameId: config.gameId,
            });
            if (result.outcome === 'no_ad' && result.reason === 'no_host') {
                // Empty-fill log — matches the host-side telemetry shape so fill rate
                // stays measurable from logs even in the dev/edge no-host case.
                this.logEmptyFill('rewarded_video', (_c = config.placementName) !== null && _c !== void 0 ? _c : config.placement, 'no_host');
            }
            const reward = {
                type: config.rewardType,
                amount: (_d = result.amount) !== null && _d !== void 0 ? _d : 0,
                granted: result.granted === true,
                reason: result.granted ? undefined : (_e = result.reason) !== null && _e !== void 0 ? _e : 'no_ad',
            };
            try {
                config.onRewarded(reward);
            }
            catch (err) {
                console.warn('[GetJarAds] onRewarded callback threw:', err);
            }
        });
    }
    /**
     * Get the current ad context (IN_GAME or ON_SITE)
     * IN_GAME: SDK is running in an iframe (embedded game)
     * ON_SITE: SDK is running directly on the page
     *
     * @returns 'IN_GAME' | 'ON_SITE'
     */
    getAdContext() {
        // SSR guard: no window → safe default
        if (typeof window === 'undefined')
            return 'ON_SITE';
        try {
            // Check if running in an iframe
            if (window.self !== window.top) {
                return 'IN_GAME';
            }
            return 'ON_SITE';
        }
        catch (e) {
            // If we can't access window.top due to cross-origin restrictions,
            // we're definitely in an iframe
            return 'IN_GAME';
        }
    }
}
GetJarAdsSDK.MAX_INIT_RETRIES = 3;
// GAM targeting constant - identifies traffic as coming from SDK
GetJarAdsSDK.SDK_SOURCE = 'sdk';
